import uniq from 'lodash.uniq';
import { IAppState } from './App-types';
import {
    unitTypes,
    UNIT_TYPE_ROYAL_GUARD_NOBILITY,
    UNIT_TYPE_ROYAL_GUARD,
} from './components/Unit/Unit-types';

const appState: IAppState = {
    state: {
        gameSettings: {
            altFootman: false,
            altLancer: false,
            altMarshal: false,
            aiMode: false,
            blitz: null,
			championsExpansion: false,
            choosePlayOrder: false,
            customUnitTypes: [],
            customUnits: false,
            draftMode: false,
            draftType: 'random',
			highSeasExpansion: false,
            masteryExpansion: false,
            nobilityExpansion: false,
            ranked: true,
            royalGuardType: UNIT_TYPE_ROYAL_GUARD_NOBILITY,
            nightfallExpansion: false,
            siegeExpansion: false,
            shockExpansion: false,
            timeLimit: 5,
            twoPlayersFullBoard: false,
            altUnits: false,
            theme: null,
			allowedRanks: [],
        },
        muted: true,
        volume: 0.45,
        volumeLevel: 4,
        showSrBanner: true,
        showReplayInstructions: false,
        dismissedAnnouncementIds: [],
    },
    mutations: {
        dismissAnnouncement(state, announcementId: number) {
            if (!state.dismissedAnnouncementIds.includes(announcementId)) {
                state.dismissedAnnouncementIds.push(announcementId);
            }
        },
        setAltUnits(state, value: boolean) {
            state.gameSettings.altUnits = value;
        },
        removeAltUnitTypes(state) {
            state.gameSettings.altFootman = false;
            state.gameSettings.altLancer = false;
            state.gameSettings.altMarshal = false;
        },
        setAltUnitType(state, altUnitType: string) {
            if (altUnitType === 'RoyalGuard') {
                state.gameSettings.royalGuardType = state.gameSettings.royalGuardType === UNIT_TYPE_ROYAL_GUARD ?
                    UNIT_TYPE_ROYAL_GUARD_NOBILITY :
                    UNIT_TYPE_ROYAL_GUARD;
            } else {
                state.gameSettings[`alt${altUnitType}`] = !state.gameSettings[`alt${altUnitType}`];
            }
        },
        setRoyalGuardType(state, type: string) {
            state.gameSettings.royalGuardType = type;
        },
        setAiMode(state, value: boolean) {
            state.gameSettings.aiMode = value;
        },
        setBlitz(state, value: number) {
            state.gameSettings.blitz = value;
        },
		setChampionsExpansion(state, value: string) {
            state.gameSettings.championsExpansion = value;
        },
        setDraftType(state, value: string) {
            state.gameSettings.draftType = value;
        },
		setHighSeasExpansion(state, value: string) {
            state.gameSettings.highSeasExpansion = value;
        },
        setMasteryExpansion(state, value: string) {
            state.gameSettings.masteryExpansion = value;
        },
        setNobilityExpansion(state, value: string) {
            state.gameSettings.nobilityExpansion = value;
        },
        setNightfallExpansion(state, value: string) {
            state.gameSettings.nightfallExpansion = value;
        },
        setSiegeExpansion(state, value: string) {
            state.gameSettings.siegeExpansion = value;
        },
        setShockExpansion(state, value: string) {
            state.gameSettings.shockExpansion = value;
        },
        setTimeLimit(state, value: number) {
            state.gameSettings.timeLimit = value;
        },
        setRanked(state, value: number) {
            state.gameSettings.ranked = value;
        },
        setTheme(state, value: string) {
            state.gameSettings.theme = value;
        },
        setMuted(state, value: string) {
            state.muted = value;
        },
        setVolume(state, value: string) {
            state.volume = value;
        },
        setVolumeLevel(state, value: string) {
            state.volumeLevel = value;
        },
        setShowSrBanner(state, value: boolean) {
            state.showSrBanner = value;
        },
        toggleCustomUnits(state, value: boolean) {
            state.gameSettings.customUnits = value;
        },
        setCustomUnitTypes(state, customUnitTypes: boolean) {
            state.gameSettings.customUnitTypes = uniq(customUnitTypes);
        },
        setShowReplayInstructions(state, value: boolean) {
            state.showReplayInstructions = value;
        },
        setChoosePlayOrder(state, value: boolean) {
            state.gameSettings.choosePlayOrder = value;
        },
        setTwoPlayersFullBoard(state, value: boolean) {
            state.gameSettings.twoPlayersFullBoard = value;
        },
		setAllowedRanks(state, value: string[]) {
            state.gameSettings.allowedRanks = value;
        },
        restoreSettings(state, prevSettings) {
            state.gameSettings.choosePlayOrder = prevSettings.choosePlayOrder;
            state.gameSettings.customUnits = prevSettings.customUnits;
            state.gameSettings.customUnitTypes = prevSettings.customUnitTypes;
            state.gameSettings.draftMode = prevSettings.draftMode;
            state.gameSettings.draftType = prevSettings.draftType;
            state.gameSettings.aiMode = !!prevSettings.aiMode;
            state.gameSettings.masteryExpansion = prevSettings.masteryExpansion;
            state.gameSettings.nobilityExpansion = prevSettings.nobilityExpansion;
            state.gameSettings.ranked = prevSettings.ranked;
            state.gameSettings.royalGuardType = prevSettings.royalGuardType;
            state.gameSettings.nightfallExpansion = prevSettings.nightfallExpansion;
            state.gameSettings.siegeExpansion = prevSettings.siegeExpansion;
            state.gameSettings.twoPlayersFullBoard = prevSettings.twoPlayersFullBoard;
            state.gameSettings.theme = prevSettings.theme;
            state.gameSettings.allowedRanks = prevSettings.allowedRanks;
        },
    },
    actions: {
        setBlitz({ commit }, value: number | null) {
            commit('setBlitz', value);
        },
        dismissAnnouncement({ commit }, announcementId: number) {
            commit('dismissAnnouncement', announcementId);
        },
        removeAltUnitTypes({ commit }) {
            commit('removeAltUnitTypes');
        },
        setAltUnits({ commit }, value: boolean) {
            commit('setAltUnits', value);
        },
        setAltUnitType({ commit }, type: string) {
            commit('setAltUnitType', type);
        },
        setRoyalGuardType({ commit }, type: string) {
            commit('setRoyalGuardType', type);
        },
        setAiMode({ commit }, value: boolean) {
            commit('setAiMode', value);
        },
        setDraftMode({ commit }, value: string) {
            commit('setDraftMode', value);
        },
        setDraftType({ commit }, value: string) {
            commit('setDraftType', value);
        },
		setChampionsExpansion({ commit }, value: boolean) {
			commit('setChampionsExpansion', value);
		},
		setHighSeasExpansion({ commit }, value: boolean) {
			commit('setHighSeasExpansion', value);
		},
        setMasteryExpansion({ commit }, value: boolean) {
            commit('setMasteryExpansion', value);
        },
        setNobilityExpansion({ commit }, value: boolean) {
            commit('setNobilityExpansion', value);
        },
        setNightfallExpansion({ commit }, value: boolean) {
            commit('setNightfallExpansion', value);
        },
        setSiegeExpansion({ commit }, value: boolean) {
            commit('setSiegeExpansion', value);
        },
        setShockExpansion({ commit }, value: boolean) {
            commit('setShockExpansion', value);
        },
        setTimeLimit({ commit }, value: number) {
            commit('setTimeLimit', value);
        },
        setRanked({ commit }, value: number) {
            commit('setRanked', value);
        },
        setMuted({ commit }, value: string) {
            commit('setMuted', value);
        },
        setVolume({ commit, state }, value: string) {
            commit('setVolume', value);

            const muted = !value;

            if (state.muted !== muted) {
                commit('setMuted', muted);
            }
        },
        setVolumeLevel({ commit }, value: string) {
            commit('setVolumeLevel', value);
        },
        setShowSrBanner({ commit }, value: boolean) {
            commit('setShowSrBanner', value);
        },
        toggleCustomUnits({ commit }, value: boolean) {
            commit('toggleCustomUnits', value);
        },
        setCustomUnitTypes({ commit }, customUnitTypes: string[]) {
            commit('setCustomUnitTypes', customUnitTypes);
        },
        setShowReplayInstructions({ commit }, value: boolean) {
            commit('setShowReplayInstructions', value);
        },
        setChoosePlayOrder({ commit }, value: boolean) {
            commit('setChoosePlayOrder', value);
        },
        setTheme({ commit }, value: string) {
            commit('setTheme', value);
        },
        setTwoPlayersFullBoard({ commit }, value: boolean) {
            commit('setTwoPlayersFullBoard', value);
        },
		setAllowedRanks({ commit }, value: string[]) {
            commit('setAllowedRanks', value);
        },
    },
    getters: {
        muted: state => state.muted,
        volume: state => state.volume,
    },
};

export default appState;
